import { Controller, useForm } from "react-hook-form";
import { LabeledInput } from "@core/components/forms/LabeledInput";
import { Button } from "@core/components/ui/button";
import { LabeledSelect } from "@core/components/forms/LabeledSelect";
import { usePaymentContacts } from "@phoenix/payments/contexts/PaymentContactsContext";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@core/components/ui/dialog";
import { AddressForm } from "@core/components/forms/Address/AddressForm";
import { useAddressBook } from "@core/hooks/useAddressBook";
import { useApi } from "@core/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ContactModel } from "@/apidtos";
import { useTranslation } from "react-i18next";

export interface ACHFormValues {
  bankAccountNumber: string;
  bankRoutingNumber: string;
  bankName: string;
  name?: string;
  billingContactId?: number;
}

interface ACHFormProps {
  includeName?: boolean;
  includeContactSelect?: boolean;
  onSubmit: (data: ACHFormValues) => void;
}

export const ACHForm = (props: ACHFormProps) => {
  const { t } = useTranslation();
  const { contacts: contextContacts } = usePaymentContacts();
  const { data: addressBookData, reload } = useAddressBook();
  const api = useApi();
  const queryClient = useQueryClient();
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: {errors},
  } = useForm<ACHFormValues>({
    defaultValues: {
      bankAccountNumber: "",
      bankRoutingNumber: "",
      bankName: "",
    },
  });

  const billingContactId = watch("billingContactId");

  // Use address book data if available, otherwise fall back to context contacts
  // This ensures we have fresh data after creating an address
  const contacts = addressBookData?.map((c: any) => c.Secondary!).filter(Boolean) ?? contextContacts ?? [];
  const contactsExist = Boolean(contacts?.length);

  // Set default billing contact when contacts become available
  useEffect(() => {
    if (props.includeContactSelect && contactsExist && !billingContactId && contacts[0]?.Id) {
      setValue("billingContactId", contacts[0].Id);
    }
  }, [contactsExist, contacts, billingContactId, props.includeContactSelect, setValue]);

  const createAddressMutation = useMutation({
    mutationFn: async (contact: ContactModel) => {
      // Set IsPrimaryBilling to true
      const contactWithBilling = {
        ...contact,
        IsPrimaryBilling: true,
      };
      const newCustomerContactId = await api["/addressbook/add"](contactWithBilling);
      // Reload address book
      reload();
      queryClient.invalidateQueries({ queryKey: ["addressBook"] });
      // Refetch to get the new contact
      const updatedAddressBook = await queryClient.fetchQuery({
        queryKey: ["addressBook"],
        queryFn: () => api["/addressbook"](),
      });
      // Find the newly created contact
      let newlyAddedContact;
      if (newCustomerContactId === 0) {
        // Guest mode - find by matching the contact we just added (should be last in list)
        newlyAddedContact = updatedAddressBook?.find((x: any) =>
          x.Secondary?.Street1 === contact.Street1 &&
          x.Secondary?.City === contact.City
        );
      } else {
        // Logged in - find by CustomerContact Id
        newlyAddedContact = updatedAddressBook?.find((x: any) => x.Id === newCustomerContactId);
      }
      return newlyAddedContact?.SecondaryId ?? newlyAddedContact?.Secondary?.Id ?? 0;
    },
  });

  const handleCreateAddress = async (contact: ContactModel) => {
    try {
      const newContactId = await createAddressMutation.mutateAsync(contact);
      setAddressModalOpen(false);
      // Wait for address book to refresh, then set the new contact
      // The query will automatically refetch due to invalidation
      await queryClient.refetchQueries({
        queryKey: ["addressBook"],
      });
      // Set the newly created contact as the selected billing contact
      // The contacts will be updated automatically from the refetched query
      if (newContactId) {
        setValue("billingContactId", newContactId, { shouldValidate: true });
      }
    } catch (err) {
      console.error("Failed to create address:", err);
    }
  };

  const submit = (data: ACHFormValues) => {
    props.onSubmit(data);
  };

  return (
    <>
      <form onSubmit={handleSubmit(submit)}>
        <div className="grid grid-cols-2 gap-2 pb-2">
          {props.includeContactSelect && (
            <div className="col-span-2">
              <Controller
                control={control}
                name="billingContactId"
                rules={{ required: props.includeContactSelect ? t("ui.billingContactRequired", "Billing contact is required") : false }}
                render={({ field }) => (
                  <LabeledSelect
                    className="col-span-2"
                    label={t("ui.billingContact", "Billing Contact")}
                    value={field.value?.toString() || (contactsExist ? contacts[0]?.Id?.toString() || "" : "")}
                    onChange={(e) => {
                      if (e.target.value === "create") {
                        setAddressModalOpen(true);
                      } else if (e.target.value) {
                        field.onChange(parseInt(e.target.value));
                      } else {
                        field.onChange(undefined);
                      }
                    }}>
                    {!contactsExist && <option value="">{t("ui.selectBillingContact", "Select Billing Contact")}</option>}
                    {contacts.map(c => (
                      <option key={c.Id} value={c.Id}>
                        {c.Street1} {c.City ? `- ${c.City}` : ""}
                      </option>
                    ))}
                    <option value="create">{t("ui.createAddress", "Create Address")}</option>
                  </LabeledSelect>
                )}
              />
            </div>
          )}
        <Controller
          control={control}
          rules={{required: "Account Owner Name Required"}}
          name="name"
          render={({ field }) => (
            <div className="col-span-2">
              <LabeledInput
                {...field}
                label="Account Owner Name">
                <div className="absolute right-2 h-full flex items-center">
                </div>
              </LabeledInput>
              {errors.name && <p className="text-red-500 text-sm">{errors.name.message}</p>}
            </div>
          )}
        />
        <Controller
          control={control}
          rules={{required: "Bank Name Required"}}
          name="bankName"
          render={({ field }) => (
            <div className="col-span-2">
              <LabeledInput
                {...field}
                label="Bank Name">
                <div className="absolute right-2 h-full flex items-center">
                </div>
              </LabeledInput>
              {errors.bankName && <p className="text-red-500 text-sm">{errors.bankName.message}</p>}
            </div>
          )}
        />
        <Controller
          control={control}
          rules={{
            required: "Routing number is required",
            pattern: {
                value: /^\d{9}$/,
                message: "Routing number must be 9 digits",
            },
          }}
          name="bankRoutingNumber"
          render={({ field }) => (
            <div className="col-span-2">
              <LabeledInput
                {...field}
                label="Routing Number">
                <div className="absolute right-2 h-full flex items-center">
                </div>
              </LabeledInput>
              {errors.bankRoutingNumber && <p className="text-red-500 text-sm">{errors.bankRoutingNumber.message}</p>}
            </div>
          )}
        />
        <Controller
          control={control}
          name="bankAccountNumber"
          rules={{
            required: "Account number is required",
            pattern: {
                value: /^\d+$/,
                message: "Account number must contain only numbers",
            },
            minLength: {
                value: 6,
                message: "Account number must be at least 6 digits",
            },
            maxLength: {
                value: 17,
                message: "Account number must be at most 17 digits",
            },
        }}
          render={({ field }) => (
            <div className="col-span-2">
              <LabeledInput
                {...field}
                label="Account Number" />
            {errors.bankAccountNumber && <p className="text-red-500 text-sm">{errors.bankAccountNumber.message}</p>}
            </div>
          )}
        />
        </div>
        <Button type="submit" disabled={Object.keys(errors).length > 0 || (props.includeContactSelect && !billingContactId)}>
          {t("ui.submit", "Submit")}
        </Button>
      </form>
      {props.includeContactSelect && (
        <Dialog open={addressModalOpen} onOpenChange={setAddressModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("ui.createAddress", "Create Address")}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="sr-only">
              {t("ui.createBillingAddress", "Create a new billing address")}
            </DialogDescription>
            <AddressForm
              id="addressModalForm"
              onSubmit={handleCreateAddress}
            />
            <DialogFooter>
              <Button type="submit" form="addressModalForm">
                {t("ui.confirm", "Confirm")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
